import {
  Button, Card, ErrorMessage,
} from 'nhsuk-react-components';
import {
  ChangeEvent, useEffect, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Back from 'components/Back';
import RoutingPlanInput from 'components/RoutingPlan/RoutingPlanInput';
import RoutingPlanList from 'components/RoutingPlan/RoutingPlanList';
import RoutingPlanUpdatesCard, { CommsUpdate } from 'components/RoutingPlan/RoutingPlanUpdatesCard';
import RoutingPlanFilter from 'components/RoutingPlan/RoutingPlanFilter';
import CampaignPageHeader from 'components/CampaignPageHeader';
import useUpdateCampaign from 'queries/mutate/updateCampaign.ts';
import ApiErrorDetails from 'components/ApiErrorDetails';
import { YYYYMMDDToUnixDate } from 'utils/formattingUtils.ts';
import { ApiError } from 'schemas/ApiError.schema.ts';
import { API_UPDATE_CAMPAIGN_ERROR, ROUTING_PLAN_ERROR_MESSAGES } from 'constants/errorMessages.ts';
import {
  getAvailableRoutingIds,
  getIterationRoutingIds,
  getIterationRulesRoutingIds,
} from 'reducers/campaign/utils.ts';
import featureFlags from 'services/featureFlags';
import { getFilteredRoutingPlans, getLatestRoutingPlans, resolveRoutingIds } from 'utils/routingPlansUtils';
import TeamRoutingPlans from 'components/TeamRoutingPlans';
import { RoutingPlans as RoutingPlansType } from 'schemas/RoutingPlan.schema';
import ProgressIndicator from 'components/ProgressIndicator/index.tsx';
import { CampaignConfig } from 'schemas/CampaignConfig.schema.ts';
import { CampaignTemplate } from 'schemas/CampaignTemplates.schema';

const getButtonLabel = (isPending: boolean, shouldShowLegacyRoutingPlans: boolean) => {
  if (isPending) {
    return 'Updating...';
  }
  if (shouldShowLegacyRoutingPlans) {
    return 'Save and continue';
  }
  return 'Confirm message plans';
};

interface RoutingPlansFormProps {
  campaign: CampaignConfig;
  teamId: string;
  campaignTemplate?: CampaignTemplate;
  teamRoutingPlans?: RoutingPlansType;
  lockError: ApiError | null;
}

function RoutingPlansForm({
  campaign, teamId, campaignTemplate, teamRoutingPlans, lockError,
}: RoutingPlansFormProps) {
  const navigate = useNavigate();
  const updateCampaign = useUpdateCampaign();
  const [error, setError] = useState<ApiError | null>(null);
  const [routingPlan, setRoutingPlan] = useState<string>('');
  const [editingRoutingPlanIndex, setEditingRoutingPlanIndex] = useState<number | null>(null);
  const [editingRoutingPlan, setEditingRoutingPlan] = useState<string>('');
  const [selectedRoutingIds, setSelectedRoutingIds] = useState<string[]>(() => resolveRoutingIds(
    campaign.CampaignConfig.AvailableRoutingIds ?? [],
    teamRoutingPlans,
  ));
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [iterationRoutingUpdates, setIterationRoutingUpdates] = useState<Array<CommsUpdate>>([]);
  const [iterationRuleRoutingUpdates, setIterationRuleRoutingUpdates] = useState<Array<CommsUpdate>>([]);
  const [maxRoutingPlansAge, setMaxRoutingPlansAge] = useState<string>('3');
  const [wordsFilter, setWordsFilter] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const teamLatestRoutingPlans = teamRoutingPlans ? getLatestRoutingPlans(teamRoutingPlans) : [];

  const shouldShowUnversionedRoutingPlans = !featureFlags.notifyRoutingPlansEnabled;

  const resetScreen = (): void => {
    setEditingRoutingPlanIndex(null);
    setEditingRoutingPlan('');
  };

  const hasPendingUpdate = (routingId: string): boolean => iterationRoutingUpdates.some((update) => update.old.includes(routingId))
  || iterationRuleRoutingUpdates.some((update) => update.old.includes(routingId));

  const isRoutingPlanPendingUpdate = (routingId: string): boolean => iterationRoutingUpdates.some((update) => update.new.includes(routingId))
  || iterationRuleRoutingUpdates.some((update) => update.new.includes(routingId));

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setRoutingPlan(e.target.value);
  };

  const handleEditInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setEditingRoutingPlan(e.target.value);
  };

  const handleAdd = (): void => {
    setErrorMessage('');
    const trimmedRoutingPlans = routingPlan.trim().split(/[\s,|]+/).filter((plan) => plan.trim().length > 0);

    const uniqueRoutingPlans = Array.from(
      trimmedRoutingPlans.reduce((map, plan) => {
        const lowerCase = plan.toLowerCase();
        if (!map.has(lowerCase)) {
          map.set(lowerCase, plan);
        }
        return map;
      }, new Map()).values(),
    );

    const duplicatesWithExisting = selectedRoutingIds.filter((plan) => uniqueRoutingPlans.includes(plan));

    if (duplicatesWithExisting.length > 0) {
      setErrorMessage(ROUTING_PLAN_ERROR_MESSAGES.duplicates);
      return;
    }

    setSelectedRoutingIds((prevSelectedRoutingIds) => [...prevSelectedRoutingIds, ...uniqueRoutingPlans]);
    setRoutingPlan('');
  };

  const handleEdit = (routingPlanIndex: number): void => {
    setErrorMessage('');
    const planToEdit = selectedRoutingIds[routingPlanIndex];

    if (hasPendingUpdate(planToEdit)) {
      setErrorMessage(ROUTING_PLAN_ERROR_MESSAGES.canOnlyEditOnce);
    } else if (isRoutingPlanPendingUpdate(planToEdit)) {
      setErrorMessage(ROUTING_PLAN_ERROR_MESSAGES.cannotEditRoutingPlanInUse.replace('%s', planToEdit));
    } else {
      setEditingRoutingPlanIndex(routingPlanIndex);
      setEditingRoutingPlan(selectedRoutingIds[routingPlanIndex]);
      setErrorMessage('');
    }
  };

  const isRoutingPlanInUse = (routingId: string): boolean => campaign.CampaignConfig.Iterations?.some((iteration) => {
    const iterationRoutings = iteration.DefaultCommsRouting?.split('|').filter(Boolean) || [];
    const ruleRoutings = iteration.IterationRules?.map((rule) => rule.CommsRouting?.split('|').filter(Boolean) || []).flat() || [];
    return iterationRoutings.includes(routingId) || ruleRoutings.includes(routingId);
  }) ?? false;

  const handleDelete = (routingId: string): void => {
    setErrorMessage('');
    const inUse = isRoutingPlanInUse(routingId) || isRoutingPlanPendingUpdate(routingId);

    if (inUse) {
      setErrorMessage(ROUTING_PLAN_ERROR_MESSAGES.cannotDeleteRoutingPlanInUse.replace('%s', routingId));
    } else {
      setSelectedRoutingIds((prev) => prev.filter((id) => id !== routingId));

      setIterationRoutingUpdates((prev) => prev.filter((update) => update.new !== routingId));
      setIterationRuleRoutingUpdates((prev) => prev.filter((update) => update.new !== routingId));
    }
  };

  const handleSaveEdit = (routingPlanIndex : number): void => {
    setErrorMessage('');
    const trimmedEditingRoutingPlan = editingRoutingPlan.trim();
    const currentRoutingPlan = selectedRoutingIds[routingPlanIndex];

    if (trimmedEditingRoutingPlan === currentRoutingPlan) {
      resetScreen();
    } else if (!trimmedEditingRoutingPlan || selectedRoutingIds.some((selectedRoutingID, idx) => selectedRoutingID === trimmedEditingRoutingPlan && idx !== routingPlanIndex)) {
      setErrorMessage(ROUTING_PLAN_ERROR_MESSAGES.duplicates);
    } else {
      if (campaign.CampaignConfig.AvailableRoutingIds && campaign.CampaignConfig.AvailableRoutingIds.length > 0) {
        const unixNow = new Date().getTime();

        campaign.CampaignConfig.Iterations?.forEach((iteration) => {
          if (!iteration.IterationDate) {
            return;
          }

          const defaultRoutings = iteration.DefaultCommsRouting?.split('|').filter(Boolean) || [];
          const old = iteration.DefaultCommsRouting || '';
          const unixIterationDate = YYYYMMDDToUnixDate(iteration.IterationDate);
          const iterationInPast = unixNow > unixIterationDate;

          if (defaultRoutings.includes(currentRoutingPlan) && !iterationInPast) {
            setIterationRoutingUpdates((prev) => [...prev, {
              name: iteration.Name || '',
              old,
              new: old.replace(currentRoutingPlan, trimmedEditingRoutingPlan),
            }]);
          }
        });

        campaign.CampaignConfig.Iterations?.forEach((iteration) => {
          iteration.IterationRules?.forEach((rule) => {
            if (!iteration.IterationDate) {
              return;
            }

            const ruleRoutings = rule.CommsRouting?.split('|').filter(Boolean) || [];
            const old = rule.CommsRouting || '';
            const unixIterationDate = YYYYMMDDToUnixDate(iteration.IterationDate);
            const iterationInPast = unixNow > unixIterationDate;

            if (ruleRoutings.includes(currentRoutingPlan) && !iterationInPast) {
              setIterationRuleRoutingUpdates((prev) => [...prev, {
                name: rule.Name || '',
                old,
                new: old.replace(currentRoutingPlan, trimmedEditingRoutingPlan),
              }]);
            }
          });
        });
      }
      setSelectedRoutingIds(selectedRoutingIds.map((selectedRoutingID, idx) => (idx === routingPlanIndex ? trimmedEditingRoutingPlan : selectedRoutingID)));
    }
  };

  const handleCancelEdit = (): void => {
    resetScreen();
  };

  const getSelectedRoutingPlans = (): RoutingPlansType => teamLatestRoutingPlans
    .filter((plan) => {
      if (plan.version) {
        return selectedRoutingIds.includes(`${plan.routing_plan_id}:${plan.version}`);
      }
      return selectedRoutingIds.includes(plan.routing_plan_id);
    });

  const handleSelectPlan = (id: string, version: number | undefined): void => {
    const uuidWithVersion = version ? `${id}:${version}` : id;
    if (!selectedRoutingIds.includes(uuidWithVersion)) {
      setSelectedRoutingIds((prevSelectedRoutingIds) => [...prevSelectedRoutingIds, uuidWithVersion]);
    }
  };
  const handleRemovePlan = (id: string, version: number | undefined): void => {
    setErrorMessage('');
    const uuidWithVersion = version ? `${id}:${version}` : id;

    const inUse = isRoutingPlanInUse(uuidWithVersion) || isRoutingPlanPendingUpdate(uuidWithVersion);

    if (inUse) {
      const planName = teamLatestRoutingPlans.find((plan) => `${plan.routing_plan_id}:${plan.version}` === uuidWithVersion)?.routing_plan_name || uuidWithVersion;
      setErrorMessage(ROUTING_PLAN_ERROR_MESSAGES.cannotDeleteRoutingPlanInUse.replace('%s', planName));
      return;
    }

    setSelectedRoutingIds((prevSelectedRoutingIds) => prevSelectedRoutingIds.filter((routingId) => routingId !== uuidWithVersion));
  };

  const buildUpdatedCampaign = () => {
    let state = getAvailableRoutingIds(campaign, selectedRoutingIds);

    if (iterationRoutingUpdates.length > 0) {
      state = getIterationRoutingIds(state, iterationRoutingUpdates);
    }

    if (iterationRuleRoutingUpdates.length > 0) {
      state = getIterationRulesRoutingIds(state, iterationRuleRoutingUpdates);
    }

    return state;
  };

  const onSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await updateCampaign.mutateAsync({
        campaignId: campaign.CampaignConfig.ID!,
        teamId: teamId!,
        campaign: buildUpdatedCampaign(),
      });

      navigate(`/${teamId}/campaign/${campaign.CampaignConfig.ID}/summary`);
    } catch {
      setError({ message: API_UPDATE_CAMPAIGN_ERROR });
    }

    setIsSubmitting(false);
  };

  useEffect(() => {
    resetScreen();
  }, [selectedRoutingIds]);

  return (
    <>
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Campaign summary" backTo={`/${teamId}/campaign/${campaign.CampaignConfig.ID}/summary`} />
      </div>

      <CampaignPageHeader campaign={campaign} template={campaignTemplate} />

      {shouldShowUnversionedRoutingPlans && (
        <>
          <Card>
            <>
              <h2>Add the message plan IDs for the campaign</h2>
              <p>
                You can add multiple message plan IDs by separating them with a space,
                a comma, , a ‘|’ separator, or by copying and pasting a list from Excel.
                The order is not important. After adding the message plan IDs, click ‘Save and continue’.
              </p>

              <RoutingPlanInput routingPlan={routingPlan} onChange={handleInputChange} onAdd={handleAdd} />

            </>
          </Card>

          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

          <RoutingPlanList
            routingPlans={selectedRoutingIds}
            editingIndex={editingRoutingPlanIndex}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            onInputChange={handleEditInputChange}
            editingValue={editingRoutingPlan}
          />
        </>
      )}

      {!shouldShowUnversionedRoutingPlans && (
        <Card>
          <>
            {!teamRoutingPlans && (
              <ProgressIndicator text="Loading message plans..." />
            )}
            {teamRoutingPlans && teamLatestRoutingPlans.length === 0 && (
              <ErrorMessage>No message plans have been setup for this team.</ErrorMessage>
            )}
            {teamRoutingPlans && teamLatestRoutingPlans.length > 0 && (
              <>
                <h2>Add message plans to this campaign</h2>
                <RoutingPlanFilter
                  maxAge={maxRoutingPlansAge}
                  wordsFilter={wordsFilter}
                  onMaxAgeChange={setMaxRoutingPlansAge}
                  onWordsFilterChange={setWordsFilter}
                />
                <TeamRoutingPlans
                  routingPlans={getFilteredRoutingPlans({ routingPlans: teamLatestRoutingPlans, maxAge: parseInt(maxRoutingPlansAge, 10), wordsFilter })}
                  selectedRoutingPlans={getSelectedRoutingPlans()}
                  onSelectPlan={handleSelectPlan}
                  onRemovePlan={handleRemovePlan}
                />
                <p className="nhsuk-u-font-weight-bold">Selected message plans</p>
                <TeamRoutingPlans routingPlans={getSelectedRoutingPlans()} onRemovePlan={handleRemovePlan} />
                {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
              </>
            )}
          </>
        </Card>
      )}

      {iterationRoutingUpdates.length > 0 && (
      <>
        <p>
          <b>
            Warning: this change affects other iterations
          </b>
          <br />
          You&#39;re changing a message plan ID that’s used in other iterations. Any changes you make here will
          apply to all
          of them. Check the details carefully before saving.
        </p>
        <RoutingPlanUpdatesCard
          title="Check the updates to the iteration message plans"
          type="Iteration"
          updates={iterationRoutingUpdates}
        />
      </>
      )}

      {
          iterationRuleRoutingUpdates.length > 0 && (
          <>
            <p>
              <b>
                Warning: this change affects other iterations
              </b>
              <br />
              You&#39;re changing a message plan ID that’s used in other iterations. Any changes you make here will
              apply to all
              of them. Check the details carefully before saving.
            </p>
            <RoutingPlanUpdatesCard
              title="Check the updates to the iteration rule message plan IDs"
              type="Iteration Rule"
              updates={iterationRuleRoutingUpdates}
            />
          </>
          )
    }

      <Button onClick={onSubmit} disabled={isSubmitting}>
        {getButtonLabel(isSubmitting, shouldShowUnversionedRoutingPlans)}
      </Button>
      <ApiErrorDetails apiError={error || lockError} />
    </>
  );
}

export default RoutingPlansForm;
